import React, { useCallback } from 'react';
import './TimeRangePills.css';

export const TIME_RANGES = [
  { key: '1w', label: '1W', days: 7 },
  { key: '1mo', label: '1M', days: 30 },
  { key: '3mo', label: '3M', days: 90 },
  { key: '6mo', label: '6M', days: 180 },
  { key: '1yr', label: '1Y', days: 365 },
  { key: '2yr', label: '2Y', days: 730 },
  { key: '3yr', label: '3Y', days: 1095 },
  { key: '5yr', label: '5Y', days: 1825 },
  { key: 'all', label: 'All', days: null },
];

/**
 * TimeRangePills - Reusable time range filter pills for transaction tables
 *
 * @param {string} selected - Currently selected range key (default: 'all')
 * @param {function} onChange - Callback with (rangeKey, cutoffDate|null)
 * @param {string} className - Optional additional CSS class
 */
const TimeRangePills = ({ selected = 'all', onChange, className = '' }) => {
  const handleClick = useCallback((range) => {
    let cutoffDate = null;
    if (range.days !== null) {
      cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - range.days);
    }
    onChange(range.key, cutoffDate);
  }, [onChange]);

  return (
    <div className={`time-range-pills ${className}`}>
      {TIME_RANGES.map((range) => (
        <button
          key={range.key}
          className={`time-range-pill ${selected === range.key ? 'active' : ''}`}
          onClick={() => handleClick(range)}
          title={range.days ? `Last ${range.label}` : 'All Time'}
        >
          {range.label}
        </button>
      ))}
    </div>
  );
};

/**
 * Helper: filter an array of objects by date against a cutoff.
 *
 * @param {Array} data
 * @param {string|string[]} dateField - Field name, or list of fields tried in
 *   order (first non-null wins). Lists are useful when the source data has
 *   sparse `purchase_order_date` but reliable `start_date`, etc.
 * @param {Date|null} cutoffDate - Drop items dated before this; null = no filter.
 *
 * Rows whose every candidate field is null/blank/unparseable are KEPT — the
 * server is the source of truth for inclusion, and silently hiding undated
 * rows when a pill is active surprises users who can see the same totals on
 * the parent surface. Only rows with a real date older than the cutoff are
 * dropped.
 */
export const filterByTimeRange = (data, dateField, cutoffDate) => {
  if (!cutoffDate) return data;
  const fields = Array.isArray(dateField) ? dateField : [dateField];
  return data.filter(item => {
    let sawAnyValidDate = false;
    for (const f of fields) {
      const val = item[f];
      if (val == null || val === '') continue;
      const d = new Date(val);
      if (isNaN(d.getTime())) continue;
      sawAnyValidDate = true;
      if (d >= cutoffDate) return true;
    }
    return !sawAnyValidDate;
  });
};

// ── helpers so callers can hand off their own period format ───────────────
// Each drilldown surface tracks the parent date filter in a slightly
// different shape (Sankey: '2years'; UnifiedTimeFilter: 'last-2-years';
// VCA: absolute start/end). These map any of those to the pill `key` set
// above so the drilldown can preselect the pill that matches the parent.

// Snap an arbitrary day count to the closest pill key, within 15% tolerance;
// anything outside the configured range collapses to 'all'.
export const daysToPillKey = (days) => {
  if (days == null || !Number.isFinite(days)) return 'all';
  let best = null;
  let bestDiff = Infinity;
  for (const r of TIME_RANGES) {
    if (r.days == null) continue;
    const diff = Math.abs(r.days - days);
    if (diff < bestDiff) { bestDiff = diff; best = r; }
  }
  if (best && bestDiff / best.days <= 0.15) return best.key;
  return 'all';
};

export const dateRangeToPillKey = (startDate, endDate) => {
  if (!startDate || !endDate) return 'all';
  const a = new Date(startDate).getTime();
  const b = new Date(endDate).getTime();
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 'all';
  return daysToPillKey(Math.round((b - a) / 86400000));
};

// UnifiedTimeFilter `period` values (e.g. 'last-2-years') → pill key.
// Also handles BiomeWorld format ('last-1-year', 'all') for drilldown sync.
export const periodValueToPillKey = (period) => {
  const map = {
    'last-30-days': '1mo',
    'last-90-days': '3mo',
    'last-6-months': '6mo',
    'last-year': '1yr',
    'last-1-year': '1yr',   // BiomeWorld format
    'last-2-years': '2yr',
    'last-3-years': '3yr',
    'last-5-years': '5yr',
    'ytd': 'all',
    'all-time': 'all',
    'all': 'all',           // BiomeWorld format
  };
  return map[period] || 'all';
};

// Pill key → BiomeWorld period value (for two-way sync).
// Returns null if the pill has no BiomeWorld equivalent (e.g. 1W, 1M).
export const pillKeyToPeriodValue = (pillKey) => {
  const map = {
    '1yr': 'last-1-year',
    '2yr': 'last-2-years',
    '3yr': 'last-3-years',
    '5yr': 'last-5-years',
    'all': 'all',
  };
  return map[pillKey] || null;
};

export const computeCutoff = (rangeKey) => {
  const r = TIME_RANGES.find(x => x.key === rangeKey);
  if (!r || r.days == null) return null;
  const d = new Date();
  d.setDate(d.getDate() - r.days);
  return d;
};

export default TimeRangePills;
